import { Card, Form, Offcanvas } from "react-bootstrap";
import CustomButton from "../../components/CustomButton/CustomButton";
import { useForm } from "react-hook-form";
import { useEffect, useRef, useState } from "react";
import ReactQuill from "react-quill-new";
import WebService from "../../utility/WebService";
import { toast } from "react-toastify";
import { DashSquareFill, PlusSquareFill } from "react-bootstrap-icons";
import CommonQuill from "../../components/editor/CommonQuill";
import HelperService from "../../utility/HelperService";

interface AddCustomizedApplicationsProps {
  show: boolean;
  handleClose: () => void;
  data?: any;
}

interface IPartner {
  id?: string;
  name: string;
  image?: string;
}

type FormData = {
  title: string;
  slug: string;
  createdBy: string;
  meta_description: string;
  keyword: string;
};

const AddCustomizedApplications = ({
  show,
  handleClose,
  data,
}: AddCustomizedApplicationsProps) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    trigger
  } = useForm<FormData>();
  const [isLoading, setLoading] = useState(false);
  const [logo, setLogo] = useState<File | null>(null);
  const [about, setAbout] = useState(data?.about || "");
  const [images, setImages] = useState<string | null>(null);
  const [fileError, setFileError] = useState<string>("");
  const [shortDescription, setShortDescription] = useState("");
  const descriptionQuillRef = useRef<any>(null);
  const shortDescQuillRef = useRef<any>(null);
  const [missionData, setMissionData] = useState<any[]>([
    { title: "", description: "", image: null },
  ]);
  const [servicesData, setServicesData] = useState<any[]>([
    { title: "", description: "", image: null },
  ]);
  const [keyFiguresData, setKeyFiguresData] = useState<any[]>([
    { title: "", image: null },
  ]);
  const [whatWeDoData, setWhatWeDoData] = useState([
    { title: "", image: null },
  ]);
  const [keyPartnersData, setKeyPartnersData] = useState([
    { title: "", image: null },
  ]);
  const [publicationsData, setPublicationsData] = useState<any[]>([
    { title: "", link: "", image: null },
  ]);
  const [newsletterData, setNewsletterData] = useState<any[]>([
    { title: "", subTitle: "", description: "", date: "", file: null },
  ]);
  const [achievementsData, setAchievementsData] = useState<any[]>([
    { title: "", description: "", image: null },
  ]);
  const [workLocationData, setWorkLocationData] = useState([{ image: null }]);
  const [performingStates, setPerformingStates] = useState<any[]>([
    {
      name: "",
      overview: "",
      image: null,
      partners: [{ name: "", image: null }],
      women_trained: "",
      income_increase: "",
      machines_deployed: "",
      energy_generated: "",
    },
  ]);
  const [customErrors, setCustomErrors] = useState<{
    about?: string;
    short_description?: string;
  }>({});
  useEffect(() => {
    if (data) {
      const storedUser = localStorage.getItem("UserData");
      const userData = storedUser ? JSON.parse(storedUser) : null;
      reset({
        title: data.title || "",
        slug: data.slug,
        keyword: data?.keyword,
        createdBy: userData?.fullName || "",
        meta_description: data?.meta_description,
      });
      setAbout(data.about || "");
      setShortDescription(data.short_description || "");

      setImages(data?.image || null);
      setMissionData(
        data.missionContent && data.missionContent.length > 0
          ? data.missionContent
          : [{ title: "", description: "", image: null }]
      );
      setServicesData(
        data.service && data.service.length > 0
          ? data.service
          : [{ title: "", description: "", image: null }]
      );
      setKeyFiguresData(
        data.keyFigures && data.keyFigures.length > 0
          ? data.keyFigures
          : [{ title: "", image: null }]
      );
      setWhatWeDoData(
        data.whatWeDo?.length ? data.whatWeDo : [{ title: "", image: null }]
      );
      setKeyPartnersData(
        data.keyPartners?.length
          ? data.keyPartners
          : [{ title: "", image: null }]
      );
      setPublicationsData(
        data.publication && data.publication.length > 0
          ? data.publication
          : [{ title: "", link: "", image: null }]
      );
      setNewsletterData(
        data.newsLetter && data.newsLetter.length > 0
          ? data.newsLetter
          : [
            {
              title: "",
              subTitle: "",
              link: "",
              description: "",
              images: [{ image: null }],
            },
          ]
      );
      setAchievementsData(
        data.achievement && data.achievement.length > 0
          ? data.achievement
          : [{ title: "", description: "", image: null }]
      );
      setWorkLocationData(
        data.workLocation?.length ? data.workLocation : [{ image: null }]
      );
      setPerformingStates(
        data?.performing_states?.length
          ? data.performing_states
          : [
            {
              name: "",
              overview: "",
              image: null,
              partners: [{ name: "", image: null }],
              women_trained: "",
              income_increase: "",
              machines_deployed: "",
              energy_generated: "",
            },
          ]
      );
    }
  }, [data, show]);
  const ALLOWED_IMAGE_TYPES = [
    "image/jpeg",
    "image/jpg",
    "image/png",
    "image/svg+xml",
  ];
  const isQuillEmpty = (value?: string) => {
    if (!value) return true;

    // remove html tags, &nbsp; and spaces
    const text = value
      .replace(/<(.|\n)*?>/g, "")
      .replace(/&nbsp;/g, "")
      .trim();

    return text.length === 0;
  };
  const validateInfoTab = async () => {
    const titleValid = await trigger(["title"]);

    const newErrors: any = {};

    if (isQuillEmpty(about)) {
      newErrors.about = "About is required";
    }

    if (isQuillEmpty(shortDescription)) {
      newErrors.short_description = "Short Description is required";
    }

    setCustomErrors(newErrors);

    // 🔥 IMPORTANT: return actual validity
    return titleValid && !newErrors.description;
  };
  const onSubmit = async (request: FormData) => {
    if (fileError) {
      toast.error(fileError);
      return;
    }
    const valid = await validateInfoTab();
    if (!valid) {
      toast.error("Please fill required fields");
      return;
    }
    setLoading(true);
    const formData = new FormData();
    const storedUser = localStorage.getItem("UserData");
    const userData = storedUser ? JSON.parse(storedUser) : null;
    formData.append("title", request.title);

    const editor = descriptionQuillRef.current?.getEditor();
    const html = editor?.root.innerHTML || "";
    formData.append("about", html);
    formData.append("meta_description", request.meta_description);
    formData.append("keyword", request.keyword);
    formData.append("slug", request.slug);
    formData.append("short_description", shortDescription);

    if (logo) {
      formData.append("logo", logo);
    }
    if (data?._id) {
      formData.append("id", data._id);
    }
    if (!data?._id && userData?._id) {
      formData.append("createdBy", userData._id);
    }
    if (images) {
      formData.append("image", images);
    }
    if (
      missionData.length > 0 &&
      missionData.every(
        (mission) =>
          mission.title.trim() !== "" ||
          mission.description.trim() !== "" ||
          (mission.image && Object.keys(mission.image).length !== 0)
      )
    ) {
      formData.append("missionContent", JSON.stringify(missionData));
    }
    if (
      servicesData.length > 0 &&
      servicesData.every(
        (service) =>
          service.title.trim() !== "" ||
          service.description.trim() !== "" ||
          (service.image && Object.keys(service.image).length !== 0)
      )
    ) {
      formData.append("service", JSON.stringify(servicesData));
    }
    if (
      keyFiguresData.length > 0 &&
      keyFiguresData.every(
        (figure) =>
          figure.title.trim() !== "" ||
          (figure.image && Object.keys(figure.image).length !== 0)
      )
    ) {
      formData.append("keyFigures", JSON.stringify(keyFiguresData));
    }
    if (
      whatWeDoData.length > 0 &&
      whatWeDoData.every(
        (item) =>
          item.title.trim() !== "" ||
          (item.image && Object.keys(item.image).length !== 0)
      )
    ) {
      formData.append("whatWeDo", JSON.stringify(whatWeDoData));
    }
    if (
      keyPartnersData.length > 0 &&
      keyPartnersData.every(
        (item) =>
          item.title.trim() !== "" ||
          (item.image && Object.keys(item.image).length !== 0)
      )
    ) {
      formData.append("keyPartners", JSON.stringify(keyPartnersData));
    }
    if (
      publicationsData.length > 0 &&
      publicationsData.every(
        (pub) =>
          pub.title.trim() !== "" ||
          pub.link.trim() !== "" ||
          (pub.image && Object.keys(pub.image).length !== 0)
      )
    ) {
      formData.append("publication", JSON.stringify(publicationsData));
    }
    if (
      newsletterData.length > 0 &&
      newsletterData.every(
        (tnw) =>
          tnw.title.trim() !== "" ||
          tnw.link.trim() !== "" ||
          tnw.description.trim() !== "" ||
          (tnw.images.length > 0 &&
            tnw.images.every(
              (img: any) => img.image && Object.keys(img.image).length !== 0
            ))
      )
    ) {
      formData.append("newsLetter", JSON.stringify(newsletterData));
    }
    if (
      achievementsData.length > 0 &&
      achievementsData.every(
        (ach) =>
          ach.title.trim() !== "" ||
          ach.description.trim() !== "" ||
          (ach.image && Object.keys(ach.image).length !== 0)
      )
    ) {
      formData.append("achievement", JSON.stringify(achievementsData));
    }
    formData.append("workLocation", JSON.stringify(workLocationData));
    if (
      performingStates.length > 0 &&
      performingStates.some((s) => s.name.trim() !== "")
    ) {
      formData.append("performing_states", JSON.stringify(performingStates));
    }

    WebService.fileUploadAPI({
      action: `customized-applications/addContent`,
      body: formData,
    })
      .then((res: any) => {
        toast.success(res.message);
        setLoading(false);
        handleClose();
      })
      .catch((err: any) => {
        setLoading(false);
        toast.error(err.response?.data?.message || "An error occurred");
      });
  };
  const imageHandler = (quillRef: any) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.click();

    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;

      const formData = new FormData();
      formData.append("file", file);
      formData.append("storage", "ABOUT_US");

      try {
        const res: any = await WebService.fileUploadAPI({
          action: "upload",
          body: formData,
        });

        const imageUrl = res?.data?.url;
        if (!imageUrl) {
          toast.error("Image upload failed");
          return;
        }

        if (!quillRef?.current) return;

        const editor = quillRef.current.getEditor();
        const range = editor.getSelection(true);

        editor.insertEmbed(range.index, "image", imageUrl);
        editor.setSelection(range.index + 1);
      } catch {
        toast.error("Image upload failed");
      }
    };
  };
  const uploadFile = (
    file: File,
    index: number,
    type: string,
    subIndex?: number
  ) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("storage", "CUSTOMIZEAPPLICATION");
    WebService.fileUploadAPI({
      action: `upload`,
      body: formData,
    })
      .then((res: any) => {
        if (res.data) {
          if (type === "GALLERY") {
            setImages(res.data?.url);
          } else if (type === "MISSION") {
            missionData[index].image = res.data?.url;
            setMissionData([...missionData]);
          } else if (type === "SERVICE") {
            servicesData[index].image = res.data?.url;
            setServicesData([...servicesData]);
          } else if (type === "KEY_FIGURE") {
            keyFiguresData[index].image = res.data?.url;
            setKeyFiguresData([...keyFiguresData]);
          } else if (type === "WHAT_WE_DO") {
            whatWeDoData[index].image = res.data?.url;
            setWhatWeDoData([...whatWeDoData]);
          } else if (type === "KEY_PARTNERS") {
            keyPartnersData[index].image = res.data?.url;
            setKeyPartnersData([...keyPartnersData]);
          } else if (type === "PUBLICATION") {
            publicationsData[index].image = res.data?.url;
            setPublicationsData([...publicationsData]);
          } else if (type === "NEWSLETTER") {
            newsletterData[index].file = res.data?.url;
            setNewsletterData([...newsletterData]);
          } else if (type === "ACHIEVEMENT") {
            achievementsData[index].image = res.data?.url;
            setAchievementsData([...achievementsData]);
          } else if (type === "WORK_LOCATION") {
            workLocationData[index].image = res.data.url;
            setWorkLocationData([...workLocationData]);
          } else if (type === "PERFORMING_STATE") {
            performingStates[index].image = res.data?.url;
            setPerformingStates([...performingStates]);
          } else if (type === "PARTNER") {
            performingStates[index].partners[subIndex!].image = res.data?.url;
            setPerformingStates([...performingStates]);
          }
        }
      })
      .catch((err: any) => {
        toast.error(err.response?.data?.message || "An error occurred");
      });
  };

  const reorderList = (
    list: any[],
    fromIndex: number,
    toIndex: number,
    setter: (val: any[]) => void
  ) => {
    if (fromIndex === toIndex) return;

    const updated = [...list];
    const [moved] = updated.splice(fromIndex, 1);
    updated.splice(toIndex, 0, moved);
    setter(updated);
  };

  return (
    <>
      <Offcanvas
        show={show}
        onHide={() => {
          handleClose();
        }}
        placement="end"
        className="offcanvas-custom"
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>{data ? "Edit" : "Add"}</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <Form>
            <Card className="p-3 bg-light my-3 position-relative">
              <span
                className="bg-white position-absolute rounded small"
                style={{ top: "-12px", left: "12px", padding: "0 8px" }}
              >
                Main Content
              </span>

              <Form.Group className="mb-2" controlId="title">
                <Form.Label className="label">Title</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter title"
                  {...register("title", { required: "Title is required" })}
                />
                {errors.title && (
                  <div className="error-message">{errors.title.message}</div>
                )}
              </Form.Group>
              <Form.Group className="mb-2" controlId="slugField">
                <Form.Label className="label">Slug</Form.Label>{" "}
                <span className="text-danger">*</span>
                <Form.Control
                  type="text"
                  placeholder="Auto-generated slug"
                  {...register("slug")}
                  value={watch("slug") || ""}
                // disabled // ✅ keep it readonly
                />
              </Form.Group>
              <Form.Group className="mb-2" controlId="about">
                <Form.Label className="label">About</Form.Label>


                <CommonQuill
                  ref={descriptionQuillRef}
                  value={about}
                  onChange={setAbout}
                  storage="CUSTOMIZEAPPLICATION"
                  initialHtml={show && data?.about ? HelperService.normalizeResizeHtml(data.about) : undefined}
                  onClearError={() =>
                    setCustomErrors((p) => ({
                      ...p,
                      about: undefined,
                    }))
                  }
                />
                {customErrors.about && (
                  <div className="error-message">
                    {customErrors.about}
                  </div>
                )}
              </Form.Group>
              <Form.Group className="mb-2" controlId="eventDescription">
                <Form.Label className="label">Short Description</Form.Label>
                <CommonQuill
                  ref={shortDescQuillRef}
                  value={shortDescription}
                  onChange={setShortDescription}
                  storage="CUSTOMIZEAPPLICATION"
                  initialHtml={show && data?.short_description ? HelperService.normalizeResizeHtml(data.short_description) : undefined}
                  onClearError={() =>
                    setCustomErrors((p) => ({
                      ...p,
                      short_description: undefined,
                    }))
                  }
                />
                {customErrors.short_description && (
                  <div className="error-message">
                    {customErrors.short_description}
                  </div>
                )}
              </Form.Group>
              <Card className="p-3 bg-light my-3 position-relative">
                <span
                  className="bg-white position-absolute rounded small"
                  style={{ top: "-12px", left: "12px", padding: "0 8px" }}
                >
                  Mission Content
                </span>
                <Form.Group className="mb-2" controlId="missionData">
                  {missionData.map((mission, index) => (
                    <div
                      key={index}
                      draggable
                      onDragStart={(e) => {
                        e.dataTransfer.setData(
                          "MissionIndex",
                          index.toString()
                        );
                      }}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => {
                        const fromIndex = Number(
                          e.dataTransfer.getData("MissionIndex")
                        );
                        if (fromIndex === index) return;
                        const newMissionData = [...missionData];
                        const [moved] = newMissionData.splice(fromIndex, 1);
                        newMissionData.splice(index, 0, moved);
                        setMissionData(newMissionData);
                      }}
                      style={{ cursor: "move" }}
                    >
                      <div className="d-flex align-items-start">
                        <span
                          style={{
                            cursor: "grab",
                            marginRight: 8,
                            display: "flex",
                            alignItems: "center",
                            fontSize: 20,
                            color: "#888",
                            marginTop: 10,
                          }}
                          title="Drag to reorder"
                        >
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <circle cx="5" cy="6" r="1.5" />
                            <circle cx="5" cy="10" r="1.5" />
                            <circle cx="5" cy="14" r="1.5" />
                            <circle cx="11" cy="6" r="1.5" />
                            <circle cx="11" cy="10" r="1.5" />
                            <circle cx="11" cy="14" r="1.5" />
                          </svg>
                        </span>
                        <div style={{ flex: 1 }} className="highlight-item">
                          <Form.Control
                            type="file"
                            accept="image/*"
                            className="custom-image-upload"
                            onChange={(e) => {
                              const input = e.target as HTMLInputElement;
                              const file = input.files?.[0];

                              if (!file) return;

                              if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
                                const msg =
                                  "Image format not supported. Please upload PNG, JPG, JPEG or SVG image.";

                                setFileError(msg);
                                toast.error(msg);
                                input.value = "";
                                return;
                              }

                              if (file) {
                                uploadFile(file, index, "MISSION");
                              }
                              setFileError("");
                            }}
                          />
                          <p
                            className="text-primary mt-1"
                            style={{ fontSize: "13px" }}
                          >
                            Note: Please upload an image of size:{" "}
                            <strong>44 × 44 px</strong> in{" "}
                            <strong>.png, .jpeg, .jpg or .svg</strong> format.
                          </p>
                          {missionData[index] && (
                            <img
                              src={
                                missionData[index].image
                                  ? missionData[index].image
                                  : data?.missionData?.[index]?.imageUrl || ""
                              }
                              style={{
                                maxWidth: "100%",
                                maxHeight: 100,
                                objectFit: "contain",
                                borderRadius: 8,
                                marginTop: 10,
                                marginBottom: 10,
                              }}
                            />
                          )}
                          <div className="d-flex">
                            <Form.Control
                              type="text"
                              placeholder={`Name`}
                              value={missionData[index].title}
                              onChange={(e) => {
                                const newMissionData = [...missionData];
                                newMissionData[index].title = e.target.value;
                                setMissionData(newMissionData);
                              }}
                            />
                            <div className="d-flex mt-2">
                              {/* <PlusSquareFill
                                onClick={() => {
                                  setMissionData([
                                    ...missionData,
                                    { title: "", description: "", image: null },
                                  ]);
                                }}
                                className="ms-2 me-2 cursor-pointer"
                              /> */}
                              {missionData.length > 1 && (
                                <DashSquareFill
                                  onClick={() => {
                                    const newMissionData = missionData.filter(
                                      (_, i) => i !== index
                                    );
                                    setMissionData(newMissionData);
                                  }}
                                  className="ms-2 me-2 cursor-pointer"
                                />
                              )}
                            </div>
                          </div>
                          <Form.Control
                            type="text"
                            placeholder={`Description`}
                            value={missionData[index].description}
                            onChange={(e) => {
                              const newMissionData = [...missionData];
                              newMissionData[index].description =
                                e.target.value;
                              setMissionData(newMissionData);
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </Form.Group>
              </Card>
              <Form.Group className="mb-2" controlId="images">
                <Form.Label className="label">Banner Image</Form.Label>

                <Form.Control
                  type="file"
                  accept="image/*"
                  className="custom-image-upload"
                  onChange={(e) => {
                    const input = e.target as HTMLInputElement;
                    const file = input.files?.[0];

                    if (!file) return;

                    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
                      const msg =
                        "Image format not supported. Please upload PNG, JPG, JPEG or SVG image.";

                      setFileError(msg);
                      toast.error(msg);
                      input.value = "";
                      return;
                    }

                    if (file) {
                      uploadFile(file, 0, "GALLERY");
                    }
                    setFileError("");
                  }}
                />
                <p
                  className="text-primary mt-1"
                  style={{ fontSize: "13px" }}
                >
                  Note: Please upload an image of size:{" "}
                  <strong>1294 × 433 px</strong> in{" "}
                  <strong>.png, .jpeg, .jpg or .svg</strong> format.
                </p>
                {images && (
                  <div className="mb-2 event-image">
                    <img
                      src={images}
                      alt="Banner"
                      style={{
                        maxWidth: "100%",
                        maxHeight: 200,
                        objectFit: "contain",
                      }}
                    />
                  </div>
                )}
              </Form.Group>
              <Card className="p-3 bg-light my-3 position-relative">
                <span
                  className="bg-white position-absolute rounded small"
                  style={{ top: "-12px", left: "12px", padding: "0 8px" }}
                >
                  Publications & Resources
                </span>
                <Form.Group
                  className="mb-2"
                  controlId="Publications & Resources"
                >
                  {publicationsData.map((publication, index) => (
                    <div
                      key={index}
                      draggable
                      onDragStart={(e) => {
                        e.dataTransfer.setData(
                          "PublicationIndex",
                          index.toString()
                        );
                      }}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => {
                        const fromIndex = Number(
                          e.dataTransfer.getData("PublicationIndex")
                        );
                        if (fromIndex === index) return;
                        const newPublicationsData = [...publicationsData];
                        const [moved] = newPublicationsData.splice(
                          fromIndex,
                          1
                        );
                        newPublicationsData.splice(index, 0, moved);
                        setPublicationsData(newPublicationsData);
                      }}
                      style={{ cursor: "move" }}
                    >
                      <div className="d-flex align-items-start">
                        <span
                          style={{
                            cursor: "grab",
                            marginRight: 8,
                            display: "flex",
                            alignItems: "center",
                            fontSize: 20,
                            color: "#888",
                            marginTop: 10,
                          }}
                          title="Drag to reorder"
                        >
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <circle cx="5" cy="6" r="1.5" />
                            <circle cx="5" cy="10" r="1.5" />
                            <circle cx="5" cy="14" r="1.5" />
                            <circle cx="11" cy="6" r="1.5" />
                            <circle cx="11" cy="10" r="1.5" />
                            <circle cx="11" cy="14" r="1.5" />
                          </svg>
                        </span>
                        <div style={{ flex: 1 }} className="highlight-item">
                          <Form.Control
                            type="file"
                            accept="image/*"
                            className="custom-image-upload"
                            onChange={(e) => {
                              const input = e.target as HTMLInputElement;
                              const file = input.files && input.files[0];
                              if (file) {
                                uploadFile(file, index, "PUBLICATION");
                              }
                            }}
                          />
                          {publicationsData[index] && (
                            <img
                              src={
                                publicationsData[index].image
                                  ? publicationsData[index].image
                                  : data?.publicationsData?.[index]?.imageUrl ||
                                  ""
                              }
                              style={{
                                maxWidth: "100%",
                                maxHeight: 100,
                                objectFit: "contain",
                                borderRadius: 8,
                                marginTop: 10,
                                marginBottom: 10,
                              }}
                            />
                          )}
                          <div className="d-flex">
                            <Form.Control
                              type="text"
                              placeholder={`Name`}
                              value={publicationsData[index].title}
                              onChange={(e) => {
                                const newPublicationsData = [
                                  ...publicationsData,
                                ];
                                newPublicationsData[index].title =
                                  e.target.value;
                                setPublicationsData(newPublicationsData);
                              }}
                            />
                            <div className="d-flex mt-2">
                              <PlusSquareFill
                                onClick={() => {
                                  setPublicationsData([
                                    ...publicationsData,
                                    { title: "", link: "", image: null },
                                  ]);
                                }}
                                className="ms-2 me-2 cursor-pointer"
                              />
                              {publicationsData.length > 1 && (
                                <DashSquareFill
                                  onClick={() => {
                                    const newPublicationsData =
                                      publicationsData.filter(
                                        (_, i) => i !== index
                                      );
                                    setPublicationsData(newPublicationsData);
                                  }}
                                  className="cursor-pointer"
                                />
                              )}
                            </div>
                          </div>
                          <Form.Control
                            type="text"
                            placeholder={`Link`}
                            value={publicationsData[index].link}
                            onChange={(e) => {
                              const newPublicationsData = [...publicationsData];
                              newPublicationsData[index].link = e.target.value;
                              setPublicationsData(newPublicationsData);
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </Form.Group>
              </Card>
              <Card className="p-3 bg-light my-3 position-relative">
                <span
                  className="bg-white position-absolute rounded small"
                  style={{ top: "-12px", left: "12px", padding: "0 8px" }}
                >
                  Newsletter
                </span>
                <span className="text-primary" style={{ fontSize: "12px" }}>
                  Note: Only PDF files are allowed. Maximum upload size is 100 MB{" "}
                </span>
                <Form.Group className="mb-2" controlId="Training and Workshop">
                  {newsletterData.map((item, index) => (
                    <div
                      key={index}
                      draggable
                      onDragStart={(e) =>
                        e.dataTransfer.setData(
                          "NewsletterIndex",
                          index.toString()
                        )
                      }
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => {
                        const fromIndex = Number(
                          e.dataTransfer.getData("NewsletterIndex")
                        );
                        reorderList(
                          newsletterData,
                          fromIndex,
                          index,
                          setNewsletterData
                        );
                      }}
                      style={{ cursor: "move" }}
                      className="mb-3 border rounded p-3"
                    >
                      <div className="d-flex align-items-start">
                        <span
                          style={{
                            cursor: "grab",
                            marginRight: 8,
                            display: "flex",
                            alignItems: "center",
                            fontSize: 20,
                            color: "#888",
                            marginTop: 10,
                          }}
                          title="Drag to reorder"
                        >
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <circle cx="5" cy="6" r="1.5" />
                            <circle cx="5" cy="10" r="1.5" />
                            <circle cx="5" cy="14" r="1.5" />
                            <circle cx="11" cy="6" r="1.5" />
                            <circle cx="11" cy="10" r="1.5" />
                            <circle cx="11" cy="14" r="1.5" />
                          </svg>
                        </span>
                        <div style={{ flex: 1 }} className="highlight-item">
                          {/* FILE */}
                          <Form.Control
                            type="file"
                            onChange={(e) => {
                              const file = (e.target as HTMLInputElement)
                                .files?.[0];
                              if (file) uploadFile(file, index, "NEWSLETTER");
                            }}
                          />

                          {item.file && (
                            <div className="mt-2">
                              <a
                                href={item.file}
                                target="_blank"
                                rel="noreferrer"
                              >
                                View File
                              </a>
                            </div>
                          )}

                          {/* TITLE */}
                          <Form.Control
                            type="text"
                            className="mt-2"
                            placeholder="Title"
                            value={item.title}
                            onChange={(e) => {
                              const updated = [...newsletterData];
                              updated[index].title = e.target.value;
                              setNewsletterData(updated);
                            }}
                          />

                          {/* SUB TITLE */}
                          <Form.Control
                            type="text"
                            className="mt-2"
                            placeholder="Sub Title"
                            value={item.subTitle}
                            onChange={(e) => {
                              const updated = [...newsletterData];
                              updated[index].subTitle = e.target.value;
                              setNewsletterData(updated);
                            }}
                          />

                          {/* DESCRIPTION */}
                          <Form.Control
                            as="textarea"
                            rows={3}
                            className="mt-2"
                            placeholder="Description"
                            value={item.description}
                            onChange={(e) => {
                              const updated = [...newsletterData];
                              updated[index].description = e.target.value;
                              setNewsletterData(updated);
                            }}
                          />

                          {/* DATE */}
                          <Form.Control
                            type="date"
                            className="mt-2"
                            value={item.date}
                            onChange={(e) => {
                              const updated = [...newsletterData];
                              updated[index].date = e.target.value;
                              setNewsletterData(updated);
                            }}
                          />

                          {/* ADD / REMOVE */}
                          <div className="d-flex mt-2">
                            <PlusSquareFill
                              className="cursor-pointer"
                              onClick={() =>
                                setNewsletterData([
                                  ...newsletterData,
                                  {
                                    title: "",
                                    description: "",
                                    date: "",
                                    file: null,
                                  },
                                ])
                              }
                            />
                            {newsletterData.length > 1 && (
                              <DashSquareFill
                                className="cursor-pointer ms-2"
                                onClick={() =>
                                  setNewsletterData(
                                    newsletterData.filter((_, i) => i !== index)
                                  )
                                }
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </Form.Group>
              </Card>
              <Card className="p-3 bg-light my-3 position-relative">
                <span
                  className="bg-white position-absolute rounded small"
                  style={{ top: "-12px", left: "12px", padding: "0 8px" }}
                >
                  Performing States
                </span>

                {performingStates.map((state, index) => (
                  <div
                    key={index}
                    draggable
                    onDragStart={(e) =>
                      e.dataTransfer.setData("stateIndex", index.toString())
                    }
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => {
                      const from = Number(e.dataTransfer.getData("stateIndex"));
                      reorderList(
                        performingStates,
                        from,
                        index,
                        setPerformingStates
                      );
                    }}
                    className="border rounded p-3 mb-3"
                    style={{ cursor: "move" }}
                  >
                    <div className="d-flex align-items-start">
                      <span
                        style={{
                          cursor: "grab",
                          marginRight: 8,
                          display: "flex",
                          alignItems: "center",
                          fontSize: 20,
                          color: "#888",
                          marginTop: 10,
                        }}
                        title="Drag to reorder"
                      >
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <circle cx="5" cy="6" r="1.5" />
                          <circle cx="5" cy="10" r="1.5" />
                          <circle cx="5" cy="14" r="1.5" />
                          <circle cx="11" cy="6" r="1.5" />
                          <circle cx="11" cy="10" r="1.5" />
                          <circle cx="11" cy="14" r="1.5" />
                        </svg>
                      </span>
                      <div style={{ flex: 1 }} className="highlight-item">
                        {/* STATE IMAGE */}
                        <Form.Control
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            const input = e.target as HTMLInputElement;
                            const file = input.files?.[0];

                            if (!file) return;

                            if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
                              const msg =
                                "Image format not supported. Please upload PNG, JPG, JPEG or SVG image.";

                              setFileError(msg);
                              toast.error(msg);
                              input.value = "";
                              return;
                            }

                            if (file) {
                              uploadFile(file, index, "PERFORMING_STATE");
                            }
                            setFileError("");
                          }}
                        />
                        <p
                          className="text-primary mt-1"
                          style={{ fontSize: "13px" }}
                        >
                          Note: Please upload an image of size:{" "}
                          <strong>341 × 364 px</strong> in{" "}
                          <strong>.png, .jpeg, .jpg or .svg</strong> format.
                        </p>

                        {state.image && (
                          <img
                            src={state.image}
                            style={{
                              maxWidth: "100%",
                              maxHeight: 150,
                              marginTop: 10,
                            }}
                          />
                        )}

                        {/* STATE NAME */}
                        <Form.Control
                          className="mt-2"
                          placeholder="State Name"
                          value={state.name}
                          onChange={(e) => {
                            const updated = [...performingStates];
                            updated[index].name = e.target.value;
                            setPerformingStates(updated);
                          }}
                        />

                        {/* OVERVIEW */}
                        <Form.Control
                          as="textarea"
                          rows={3}
                          className="mt-2"
                          placeholder="Overview"
                          value={state.overview}
                          onChange={(e) => {
                            const updated = [...performingStates];
                            updated[index].overview = e.target.value;
                            setPerformingStates(updated);
                          }}
                        />

                        {/* NUMBERS */}
                        <div className="row mt-2">
                          {[
                            ["women_trained", "Women Trained"],
                            ["income_increase", "Income Increase"],
                            ["machines_deployed", "Machines Deployed"],
                            ["energy_generated", "Energy Generated"],
                          ].map(([key, label]) => (
                            <div className="col-md-6 mt-2" key={key}>
                              <Form.Control
                                type="text"
                                placeholder={label}
                                value={state[key]}
                                onChange={(e) => {
                                  const updated = [...performingStates];
                                  updated[index][key] = e.target.value;
                                  setPerformingStates(updated);
                                }}
                              />
                            </div>
                          ))}
                        </div>

                        {/* PARTNERS */}
                        <div className="mt-3">
                          <strong>Partners</strong>

                          {state.partners.map(
                            (partner: IPartner, pIndex: number) => (
                              <div
                                key={pIndex}
                                draggable
                                onDragStart={(e) =>
                                  e.dataTransfer.setData(
                                    "partnerIndex",
                                    `${index}-${pIndex}`
                                  )
                                }
                                onDragOver={(e) => e.preventDefault()}
                                onDrop={(e) => {
                                  const [sIdx, fromP] = e.dataTransfer
                                    .getData("partnerIndex")
                                    .split("-")
                                    .map(Number);

                                  if (sIdx !== index) return;

                                  const updated = [...performingStates];
                                  const partners = [...updated[index].partners];
                                  const [moved] = partners.splice(fromP, 1);
                                  partners.splice(pIndex, 0, moved);
                                  updated[index].partners = partners;
                                  setPerformingStates(updated);
                                }}
                                className="border rounded p-2 my-2"
                                style={{ cursor: "move" }}
                              >
                                <div style={{ cursor: "grab", color: "#888" }}>
                                  ☰
                                </div>

                                <Form.Control
                                  placeholder="Partner Name"
                                  value={partner.name}
                                  onChange={(e) => {
                                    const updated = [...performingStates];
                                    updated[index].partners[pIndex].name =
                                      e.target.value;
                                    setPerformingStates(updated);
                                  }}
                                />

                                <Form.Control
                                  type="file"
                                  className="mt-2"
                                  accept="image/*"
                                  onChange={(e) => {
                                    const file = (e.target as HTMLInputElement)
                                      .files?.[0];
                                    if (file)
                                      uploadFile(
                                        file,
                                        index,
                                        "PARTNER",
                                        pIndex
                                      );
                                  }}
                                />
                                <p
                                  className="text-primary mt-1"
                                  style={{ fontSize: "13px" }}
                                >
                                  Note: Please upload an image of size:{" "}
                                  <strong>69 × 74 px</strong> in{" "}
                                  <strong>.png, .jpeg, .jpg or .svg</strong>{" "}
                                  format.
                                </p>

                                {partner.image && (
                                  <img
                                    src={partner.image}
                                    style={{
                                      maxWidth: "100%",
                                      maxHeight: 80,
                                      marginTop: 8,
                                    }}
                                  />
                                )}

                                {state.partners.length > 1 && (
                                  <DashSquareFill
                                    className="cursor-pointer mt-2"
                                    onClick={() => {
                                      const updated = [...performingStates];
                                      updated[index].partners = updated[
                                        index
                                      ].partners.filter(
                                        (_: any, i: number) => i !== pIndex
                                      );
                                      setPerformingStates(updated);
                                    }}
                                  />
                                )}
                              </div>
                            )
                          )}

                          <PlusSquareFill
                            className="cursor-pointer mt-2"
                            onClick={() => {
                              const updated = [...performingStates];
                              updated[index].partners.push({
                                name: "",
                                image: null,
                              });
                              setPerformingStates(updated);
                            }}
                          />
                        </div>

                        {/* ADD / REMOVE STATE */}
                        <div className="d-flex mt-3">
                          <PlusSquareFill
                            className="cursor-pointer"
                            onClick={() =>
                              setPerformingStates([
                                ...performingStates,
                                {
                                  name: "",
                                  overview: "",
                                  image: null,
                                  partners: [{ name: "", image: null }],
                                },
                              ])
                            }
                          />

                          {performingStates.length > 1 && (
                            <DashSquareFill
                              className="cursor-pointer ms-2"
                              onClick={() =>
                                setPerformingStates(
                                  performingStates.filter((_, i) => i !== index)
                                )
                              }
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </Card>
              <Form.Group className="mb-2" controlId="createdBy">
                <Form.Label className="label">Created By</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Created by user"
                  {...register("createdBy")}
                  value={watch("createdBy") || ""}
                  disabled
                />
              </Form.Group>
            </Card>
            <Card className="p-3 bg-light my-3 position-relative">
              <span
                className="bg-white position-absolute rounded small"
                style={{ top: "-12px", left: "12px", padding: "0 8px" }}
              >
                Media
              </span>
              <Form.Group className="mb-2" controlId="logo">
                <Form.Label className="label">logo</Form.Label>
                <Form.Control
                  type="file"
                  accept="image/*"
                  className="custom-image-upload"
                  onChange={(e) => {
                    const input = e.target as HTMLInputElement;
                    const file = input.files && input.files[0];
                    if (file) {
                      setLogo(file);
                    }
                  }}
                />
                <p
                  className="text-primary mt-1"
                  style={{ fontSize: "13px" }}
                >
                  Note: Please upload an image of size:{" "}
                  <strong>252 × 103 px</strong> in{" "}
                  <strong>.png, .jpeg, .jpg or .svg</strong> format.
                </p>
              </Form.Group>
              {(logo || data?.logo) && (
                <div className="mb-2 event-image">
                  <img
                    src={logo ? URL.createObjectURL(logo) : data?.logo}
                    alt="Event"
                    style={{
                      maxWidth: "100%",
                      maxHeight: 200,
                      objectFit: "contain",
                    }}
                  />
                </div>
              )}
            </Card>

            <Card className="p-3 bg-light my-3 position-relative">
              <span
                className="bg-white position-absolute rounded small"
                style={{ top: "-12px", left: "12px", padding: "0 8px" }}
              >
                SEO
              </span>
              <Form.Label className="label">
                Meta Description
                <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                as="textarea"
                placeholder="Meta Description"
                {...register("meta_description", {
                  required: "Meta description is required",
                })}
              />
              {errors.meta_description && (
                <div className="error-message">
                  {errors.meta_description.message}
                </div>
              )}
              <Form.Group className="mb-2" controlId="eventName">
                <Form.Label className="label">
                  Keywords <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter keywords"
                  {...register("keyword", {
                    required: "Keywords are required",
                  })}
                />
                {errors.keyword && (
                  <div className="error-message">{errors.keyword.message}</div>
                )}
              </Form.Group>
            </Card>
          </Form>
        </Offcanvas.Body>
        <div className="d-flex justify-content-end p-3">
          <CustomButton
            isLoading={isLoading}
            label="Save"
            className="w-100"
            onClick={handleSubmit(onSubmit)}
          />
        </div>
      </Offcanvas>
    </>
  );
};

export default AddCustomizedApplications;
